import React from 'react';
import Icon from '../../../components/AppIcon';

const ContractorMetrics = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 4.5) return 'text-success';
    if (score >= 4.0) return 'text-primary';
    if (score >= 3.5) return 'text-warning';
    return 'text-error';
  };

  const getScoreBgColor = (score) => {
    if (score >= 4.5) return 'bg-success/10 border-success/20';
    if (score >= 4.0) return 'bg-primary/10 border-primary/20';
    if (score >= 3.5) return 'bg-warning/10 border-warning/20';
    return 'bg-error/10 border-error/20';
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="HardHat" size={24} className="text-primary" />
        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground">Contractor Performance</h3>
          <p className="text-xs text-muted-foreground">Top performers ranked by overall rating</p>
        </div>
      </div>

      {data?.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <Icon name="Users" size={48} className="mb-2 opacity-50" />
          <p className="text-sm">No contractor data available</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ background: 'linear-gradient(135deg, #1B3A5C 0%, #2D5580 100%)' }}>
                  <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-wider text-white/90">Rank</th>
                  <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-wider text-white/90">Contractor</th>
                  <th className="text-center py-3 px-2 text-xs font-semibold uppercase tracking-wider text-white/90">Rating</th>
                  <th className="text-center py-3 px-2 text-xs font-semibold uppercase tracking-wider text-white/90">Quality</th>
                  <th className="text-center py-3 px-2 text-xs font-semibold uppercase tracking-wider text-white/90">On-Time</th>
                  <th className="text-center py-3 px-2 text-xs font-semibold uppercase tracking-wider text-white/90">Satisfaction</th>
                  <th className="text-center py-3 px-2 text-xs font-semibold uppercase tracking-wider text-white/90">Completion</th>
                  <th className="text-center py-3 px-2 text-xs font-semibold uppercase tracking-wider text-white/90">Projects</th>
                </tr>
              </thead>
              <tbody>
                {data?.map((contractor, index) => (
                  <tr key={index} className="border-b border-border hover:bg-muted/50 transition-colors">
                    <td className="py-3 px-2">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                        {index + 1}
                      </div>
                    </td>
                    <td className="py-3 px-2">
                      <p className="font-medium text-foreground text-sm">{contractor?.name}</p>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Icon name="Star" size={14} className="text-accent fill-accent" />
                        <span className={`font-semibold text-sm ${getScoreColor(contractor?.rating)}`}>
                          {contractor?.rating?.toFixed(1)}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className={`font-medium text-sm ${getScoreColor(contractor?.qualityScore)}`}>
                        {contractor?.qualityScore?.toFixed(1)}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className="text-sm font-medium text-foreground">
                        {contractor?.timelineAdherence?.toFixed(0)}%
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className={`font-medium text-sm ${getScoreColor(contractor?.customerSatisfaction)}`}>
                        {contractor?.customerSatisfaction?.toFixed(1)}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className="text-sm font-medium text-foreground">
                        {contractor?.completionRate?.toFixed(0)}%
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className="text-sm text-muted-foreground">
                        {contractor?.completedProjects}/{contractor?.totalProjects}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {data?.map((contractor, index) => (
              <div key={index} className={`border rounded-lg p-4 ${getScoreBgColor(contractor?.rating)}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/20 text-primary font-semibold text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{contractor?.name}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <Icon name="Star" size={12} className="text-accent fill-accent" />
                        <span className={`font-semibold text-sm ${getScoreColor(contractor?.rating)}`}>
                          {contractor?.rating?.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">Quality:</span>
                    <span className={`ml-1 font-medium ${getScoreColor(contractor?.qualityScore)}`}>
                      {contractor?.qualityScore?.toFixed(1)}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">On-Time:</span>
                    <span className="ml-1 font-medium text-foreground">
                      {contractor?.timelineAdherence?.toFixed(0)}%
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Satisfaction:</span>
                    <span className={`ml-1 font-medium ${getScoreColor(contractor?.customerSatisfaction)}`}>
                      {contractor?.customerSatisfaction?.toFixed(1)}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Completion:</span>
                    <span className="ml-1 font-medium text-foreground">
                      {contractor?.completionRate?.toFixed(0)}%
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Projects:</span>
                    <span className="ml-1 font-medium text-foreground">
                      {contractor?.completedProjects}/{contractor?.totalProjects}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorMetrics;